import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,

  Alert,
  Tooltip,
} from '@mui/material'
import {
  TableChart as ExcelIcon,
  Assessment as ReportIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import ReportsTabs from '../../components/reports/ReportsTabs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import { useBalanceSheetReport, useGenerateBalanceSheetExcel } from '../../hooks/api/useReports'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'

// ─── Sub-components ────────────────────────────────────────────────────────────

const SectionHeader = ({ label }: { label: string }) => (
  <Typography
    variant="body2"
    fontWeight="bold"
    sx={{
      mt: 2,
      mb: 0.5,
      px: 1,
      py: 0.5,
      bgcolor: '#f7f5f2',
      borderRadius: 0.5,
      letterSpacing: 0.5,
      color: 'text.primary',
    }}
  >
    {label}
  </Typography>
)

const SubHeader = ({ label }: { label: string }) => (
  <Typography
    variant="body2"
    fontWeight="bold"
    sx={{ mt: 1.5, mb: 0.25, pl: 1, color: 'text.secondary' }}
  >
    {label}
  </Typography>
)

const LineRow = ({
  label,
  value,
  indent = 1,
  note,
}: {
  label: string
  value: number
  indent?: number
  note?: string
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pl: indent * 2,
      py: 0.4,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {note && (
        <Tooltip title={note} placement="right">
          <InfoIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
      {formatCurrency(value)}
    </Typography>
  </Box>
)

const TotalRow = ({
  label,
  amount,
  size = 'normal',
  color,
}: {
  label: string
  amount: number
  size?: 'normal' | 'large'
  color?: string
}) => (
  <>
    <Divider sx={{ mt: 0.75, mb: 0.25 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography
        variant={size === 'large' ? 'subtitle1' : 'body2'}
        fontWeight="bold"
        color={color ?? 'text.primary'}
      >
        {label}
      </Typography>
      <Typography
        variant={size === 'large' ? 'subtitle1' : 'body2'}
        fontWeight="bold"
        color={color ?? 'text.primary'}
        sx={{ ml: 2, whiteSpace: 'nowrap' }}
      >
        {formatCurrency(amount)}
      </Typography>
    </Box>
  </>
)

// ─── Main Page ─────────────────────────────────────────────────────────────────

const BalanceSheetReportPage = () => {
  const [asOfDate, setAsOfDate] = useState<Date | null>(new Date())
  const [viewReport, setViewReport] = useState(false)

  const formattedDate = asOfDate ? format(asOfDate, 'yyyy-MM-dd') : ''

  const { data, isLoading } = useBalanceSheetReport(
    viewReport && formattedDate ? { asOfDate: formattedDate } : undefined
  )

  const excelMutation = useGenerateBalanceSheetExcel()

  const handleViewReport = () => {
    if (asOfDate) setViewReport(true)
  }

  const handleExport = () => {
    if (formattedDate) excelMutation.mutate({ asOfDate: formattedDate })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ReportIcon /> Reports
        </Typography>

        <ReportsTabs activeTab={4} />

        {/* Filter bar */}
        <Paper
          sx={{ p: 2.5, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <DatePicker
            label="As of Date"
            value={asOfDate}
            onChange={(v) => {
              setAsOfDate(v)
              setViewReport(false)
            }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            onClick={handleViewReport}
            disabled={!asOfDate || isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : <ReportIcon />}
          >
            View Report
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExport}
            disabled={!asOfDate || excelMutation.isPending}
            startIcon={excelMutation.isPending ? <CircularProgress size={18} /> : <ExcelIcon />}
          >
            Export Excel
          </Button>
        </Paper>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Generating report…
            </Typography>
          </Box>
        )}

        {/* Balance Sheet */}
        {viewReport && data && !isLoading && (
          <Box>
            {/* Balance mismatch warning */}
            {!data.is_balanced && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 960, mx: 'auto' }}>
                Assets do not equal Liabilities + Equity. This may indicate incomplete or
                inconsistent data.
              </Alert>
            )}

            <Paper sx={{ p: { xs: 3, sm: 5 }, maxWidth: 960, mx: 'auto' }}>
              {/* Company header */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {data.company.name}
                </Typography>
                <Typography variant="h6" fontWeight="medium">
                  Balance Sheet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  As of {format(new Date(asOfDate!), 'MMMM d, yyyy')}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Two-column layout */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: { xs: 4, md: 6 },
                }}
              >
                {/* ── LEFT: ASSETS ─────────────────────────────────────────── */}
                <Box>
                  <SectionHeader label="ASSETS" />
                  <SubHeader label="Current Assets" />

                  <LineRow
                    label="Cash"
                    value={data.assets.cash}
                    indent={2}
                    note="Cash collected from sales settlements. Does not represent a bank account balance."
                  />
                  <LineRow
                    label="Accounts Receivable"
                    value={data.assets.accounts_receivable}
                    indent={2}
                  />
                  <LineRow
                    label="Inventory (at cost)"
                    value={data.assets.inventory}
                    indent={2}
                    note={`Purchased: ${formatCurrency(data.inventory_detail.total_purchased)} − COGS to date: ${formatCurrency(data.inventory_detail.cogs_to_date)}`}
                  />

                  <TotalRow
                    label="Total Assets"
                    amount={data.assets.total}
                    size="large"
                    color="primary.main"
                  />
                </Box>

                {/* ── RIGHT: LIABILITIES + EQUITY ──────────────────────────── */}
                <Box>
                  <SectionHeader label="LIABILITIES" />
                  <SubHeader label="Current Liabilities" />

                  <LineRow
                    label="Accounts Payable"
                    value={data.liabilities.accounts_payable}
                    indent={2}
                  />

                  <TotalRow label="Total Liabilities" amount={data.liabilities.total} />

                  <SectionHeader label="EQUITY" />

                  <LineRow
                    label="Equity"
                    value={data.equity.retained_equity}
                    indent={2}
                    note="Total Assets minus Total Liabilities"
                  />

                  <TotalRow label="Total Equity" amount={data.equity.total} />

                  {/* Double-rule total */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2,
                      py: 0.75,
                      borderTop: 3,
                      borderBottom: 3,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total Liabilities + Equity
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ ml: 2, whiteSpace: 'nowrap' }}
                    >
                      {formatCurrency(data.liabilities.total + data.equity.total)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mt: 4, mb: 2 }} />

              {/* Balance check */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  color={data.is_balanced ? 'success.main' : 'error.main'}
                  fontWeight="medium"
                >
                  {data.is_balanced
                    ? '✓ Balance sheet is balanced'
                    : '⚠ Assets ≠ Liabilities + Equity'}
                </Typography>
              </Box>

              {/* Cash disclaimer */}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', mt: 1 }}
              >
                Note: "Cash" represents cash collected from sales settlements, not a bank account
                balance.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Zero state */}
        {viewReport && !isLoading && !data && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography color="text.secondary">
              No data found for the selected date.
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default BalanceSheetReportPage
